import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Play } from "lucide-react";
import VideoModal from "./VideoModal";
import { scrollToId } from "@/data";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 42, damping: 22 });
  const sy = useSpring(my, { stiffness: 42, damping: 22 });

  const onMouseMove = (e) => {
    mx.set((e.clientX / window.innerWidth - 0.5) * 20);
    my.set((e.clientY / window.innerHeight - 0.5) * 14);
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative h-[100svh] min-h-[640px] overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          src="/images/hero.jpg"
          alt="An Indian classroom where a teacher and students explore a 3D human heart through AuraXR"
          data-testid="hero-image"
          style={{ x: sx, y: sy }}
          className="h-full w-full object-cover scale-[1.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050811]/95 via-[#050811]/45 to-[#050811]/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050811] via-transparent to-[#050811]/40" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 h-full mx-auto max-w-[1440px] px-6 lg:px-12 flex flex-col justify-end pb-[12vh]"
      >
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="mb-6 text-sm tracking-[0.08em] text-slate-300/90"
        >
          An education-focused AR concept
        </motion.p>

        <h1
          data-testid="hero-heading"
          className="font-display text-[#FFFDF9] text-[11vw] sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight"
        >
          <MaskedLine delay={0.25}>Where learning</MaskedLine>
          <MaskedLine delay={0.42}>
            comes to <span className="italic text-[#38BDF8]">life</span>.
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subtext"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75 }}
          className="mt-7 max-w-md text-base sm:text-lg text-slate-200/90 leading-relaxed"
        >
          AuraXR brings visual learning into the real classroom — helping teachers explain and students understand.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-6"
        >
          <button
            data-testid="hero-cta-video-modal"
            onClick={() => setVideoOpen(true)}
            className="group flex items-center gap-3 rounded-full bg-[#38BDF8] pl-6 pr-7 py-3.5 text-sm font-semibold text-[#050811] transition-all duration-300 hover:bg-[#7dd3fc] hover:shadow-[0_0_40px_rgba(56,189,248,0.35)]"
          >
            <Play size={15} className="fill-current transition-transform duration-300 group-hover:scale-125" />
            See AuraXR in Action
          </button>
          <button
            data-testid="hero-cta-explore-classroom"
            onClick={() => scrollToId("why")}
            className="group text-sm text-slate-200 hover:text-[#38BDF8] transition-colors duration-300 tracking-wide"
          >
            Explore the classroom
            <span className="inline-block ml-2 transition-transform duration-300 group-hover:translate-y-1">↓</span>
          </button>
        </motion.div>
      </motion.div>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} />
    </section>
  );
}
